import { useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  BudgetNotificationSettings,
  BudgetResponse,
  BudgetWithIds,
} from 'typings'
import { useFetch } from '../useFetch'
import { BudgetMapper } from './BudgetMapper'
import { BUDGET_URL } from './constants'

const PROMOTION_ID_MOCK = 'new-promotion'

type SaveMutationProps = {
  amount: number
  promotionId?: string
  promotionName: string
  notification?: BudgetNotificationSettings
}

type UpdateMutationProps = BudgetWithIds & {
  promotionId?: string
  promotionName: string
}

export function useBudgetMutation() {
  const { fetcherWrapper } = useFetch()
  const queryClient = useQueryClient()

  const saveMutation = async (params: SaveMutationProps) => {
    const { amount, promotionId, promotionName, notification } = params

    const url = new URL(`${BUDGET_URL}`, window.location.origin)

    const payload = BudgetMapper.toAPI({
      amount,
      promotionId: promotionId ?? PROMOTION_ID_MOCK,
      promotionName,
      notification,
    })

    const res = await fetcherWrapper(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
    return res
  }

  const { mutateAsync: saveAsync, isPending: isPendingSave } = useMutation({
    mutationFn: saveMutation,
  })

  const save = async (
    params: SaveMutationProps,
    options?: {
      onSuccess?: (response: BudgetWithIds) => void
      onError?: (error: Error) => void
    }
  ) => {
    try {
      const response = await saveAsync(params)
      const budgetResponse: BudgetResponse = await response.json()

      // This asssumes we have a 1:1 relation on budget/allocation
      const budget = BudgetMapper.fromAPI(budgetResponse, budgetResponse.allocations[0]) 

      options?.onSuccess?.(budget)
      return budget
    } catch (error) {
      options?.onError?.(error as Error)
      throw error
    }
  }

  const updateMutation = async (params: UpdateMutationProps) => {
    const { id, allocationId, amount, notification, promotionId, promotionName } =
      params

    const payload = BudgetMapper.toAPI({
      amount,
      promotionId: promotionId ?? PROMOTION_ID_MOCK,
      promotionName,
      notification,
    })

    const url = new URL(
      `${BUDGET_URL}/${id}/allocation/${allocationId}`,
      window.location.origin
    )

    return fetcherWrapper(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
  }

  const { mutateAsync: updateAsync, isPending: isPendingUpdate } = useMutation({
    mutationFn: updateMutation,
  })

  const update = async (
    params: UpdateMutationProps,
    options?: {
      onSuccess?: (response: BudgetWithIds) => void
      onError?: (error: Error) => void
    }
  ) => {
    try {
      const response = await updateAsync(params)
      const budgetResponse: BudgetResponse = await response.json()
      const data: BudgetWithIds = BudgetMapper.fromAPI(
        budgetResponse,
        budgetResponse.allocations[0] // This asssumes we have a 1:1 relation on budget/allocation
      )
      queryClient.setQueryData(
        [`budget-${data.id}`, data.allocationId],
        data
      )
      options?.onSuccess?.(data)
      return data
    } catch (error) {
      options?.onError?.(error as Error)
      throw error
    }
  }

  const deleteMutation = (id: string) => {
    const url = new URL(`${BUDGET_URL}/${id}`, window.location.origin)

    return fetcherWrapper(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    })
  }

  const { mutateAsync: removeAsync, isPending: isPendingDelete } = useMutation({
    mutationFn: deleteMutation,
  })

  const remove = async (
    id: string,
    options?: {
      onSuccess?: () => void
      onError?: (error: Error) => void
    }
  ) => {
    try {
      await removeAsync(id)
      options?.onSuccess?.()
      return
    } catch (error) {
      options?.onError?.(error as Error)
      throw error
    }
  }

  return {
    save,
    update,
    remove,
    isLoading: isPendingSave || isPendingUpdate || isPendingDelete,
  }
}
